import { isGuideTourTargetInsideActiveModal } from '../routing/activation'
import {
  hasGuideTourTargetPositionDrifted,
  waitForGuideTourTargetPositionStability,
} from './targetVisibility'
import {
  commitGuideTourVisibleTargetBounds,
  handleGuideTourPendingScrollReveal,
  handleGuideTourTargetNotVisible,
  isGuideTourTargetVisibleForMeasurement,
  trySyncRevealedTargetWhileScrollLocked,
  tryWaitForGuideTourInitialLayoutStability,
  type GuideTourTargetMeasurementContext,
} from './guideTourTargetMeasurementHandlers'
import {
  findGuideTourTargetElement,
  getGuideTourTargetBounds,
  resolveGuideTourTargetRect,
  type SpotlightBounds,
} from './utils'
import { ignoreRejectedGuideTourPromise } from './guideTourTargetScrollFlow'

export type GuideTourTargetMeasurementState = {
  hasRevealedBounds: boolean
  lastRevealedBounds: SpotlightBounds | null
  pendingScrollReveal: boolean
  isAwaitingReveal: boolean
  initialStabilityDone: boolean
  awaitingInitialStability: boolean
  scrollAttempt: number
}

export type MeasureGuideTourTargetInput = {
  targetId: string
  forceScrollToTarget: boolean
  scrollTargetId: string | null
  state: GuideTourTargetMeasurementState
  getHasRevealedBounds: () => boolean
  getLastRevealedBounds: () => SpotlightBounds | null
  setBounds: (bounds: SpotlightBounds | null) => void
  startScrollPolling: () => void
  stopScrollPolling: () => void
  keepTargetInViewWhileLocked: (element: HTMLElement) => void
  requestProgrammaticScroll: (element: HTMLElement) => void
  scheduleRevealAfterScroll: (abort: { aborted: boolean }) => void
  isScrollRequestStale: () => boolean
  maxScrollAttempts: number
  /** Re-run measurement and sync hook refs (never call measureGuideTourTarget alone). */
  rescheduleMeasure: () => void
  persistMeasurementState: (state: GuideTourTargetMeasurementState) => void
}

const getViewportSize = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
})

const waitForGuideTourInitialLayoutStability = (
  element: HTMLElement,
  input: MeasureGuideTourTargetInput
): void => {
  if (input.state.awaitingInitialStability) {
    return
  }

  input.state.awaitingInitialStability = true

  waitForGuideTourTargetPositionStability(element)
    .then(() => {
      input.state.awaitingInitialStability = false
      input.state.initialStabilityDone = true
      input.persistMeasurementState(input.state)

      if (input.isScrollRequestStale()) {
        return
      }

      input.rescheduleMeasure()
    })
    .catch(ignoreRejectedGuideTourPromise)
}

const buildMeasurementContext = (
  element: HTMLElement,
  viewport: ReturnType<typeof getViewportSize>
): GuideTourTargetMeasurementContext | null => {
  const rect = resolveGuideTourTargetRect(element)
  const hasMeasurableSize = rect.width > 0 && rect.height > 0

  if (!hasMeasurableSize) {
    return null
  }

  const nextBounds = getGuideTourTargetBounds(element)
  const isInsideActiveModal = isGuideTourTargetInsideActiveModal(element)
  const isVisible = isGuideTourTargetVisibleForMeasurement(nextBounds, viewport)

  return {
    element,
    nextBounds,
    viewport,
    hasMeasurableSize,
    isInsideActiveModal,
    isVisible,
    positionDrifted: false,
  }
}

export const measureGuideTourTarget = (
  input: MeasureGuideTourTargetInput
): void => {
  const element = findGuideTourTargetElement(input.targetId)

  if (!element) {
    input.setBounds(null)
    return
  }

  const viewport = getViewportSize()
  const baseContext = buildMeasurementContext(element, viewport)

  if (!baseContext) {
    input.setBounds(null)
    return
  }

  const context: GuideTourTargetMeasurementContext = {
    ...baseContext,
    positionDrifted: hasGuideTourTargetPositionDrifted(
      input.getLastRevealedBounds(),
      baseContext.nextBounds
    ),
  }

  if (trySyncRevealedTargetWhileScrollLocked(input, context)) {
    return
  }

  if (handleGuideTourTargetNotVisible(input, context)) {
    return
  }

  if (handleGuideTourPendingScrollReveal(input)) {
    return
  }

  if (
    tryWaitForGuideTourInitialLayoutStability(input, context, el =>
      waitForGuideTourInitialLayoutStability(el, input)
    )
  ) {
    return
  }

  commitGuideTourVisibleTargetBounds(input, context.nextBounds)
}
